import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createStaffMember, getStaffMembers, updateStaffMember, resetStaffPassword } from '../../pages/app/protected/services/staffService';
import { FiPlus, FiEdit ,FiTrash, FiSearch, FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import StaffForm from './StaffForm';

export default function StaffManagement() {
  const { companyId } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load staff
  useEffect(() => { 
    if (companyId) loadStaff();
  }, [companyId]);

  const loadStaff = async () => {
    try {
      const staffList = await getStaffMembers(companyId);
      setStaff(staffList);
    } catch (err) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      await updateStaffMember(member.id, { isActive: !member.isActive });
      toast.success(`Staff ${member.isActive ? 'deactivated' : 'activated'}`);
      loadStaff();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateStaff = async (staffData) => {
    try {
      await createStaffMember({ ...staffData, companyId: companyId });
      toast.success('Staff created!');
      setShowForm(false);
      loadStaff();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleResetPassword = async (email) => {
    if (window.confirm(`Send password reset to ${email}?`)) {
      try {
        await resetStaffPassword(email);
        toast.success('Password reset sent');
      } catch (err) {
        toast.error('Failed to send reset');
      }
    }
  };

  const handleDeleteMember = async (id) => {
    if (window.confirm(`Delete member ${id}?`)) {
      try {
        await deleteStaffMember(id);
        toast.success('Member deleted!');
        loadStaff();
      } catch (err) {
        toast.error('Failed to delete member');
      }
    }
  };

  const handleUpdateMember = async (id) => {
    if (window.confirm(`Update member ${id}?`)) {
      try {
        await updateStaffMember(id);
        toast.success('Member updated!');
        loadStaff();
      } catch (err) {
        toast.error('Failed to update member');
      }
    }
  };

  const filteredStaff = staff.filter(member => 
    `${member.staffId} ${member.email} ${member.role}`.toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl text-white font-bold">List of Members</h1>
        <div className="flex gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <FiPlus className="mr-2" /> Add Staff
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6">
          <StaffForm
            onSubmit={handleCreateStaff}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredStaff.map((member) => (
              <tr key={member.id} className={!member.isActive ? 'bg-gray-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">{member.staffId}</td>
                <td className="px-6 py-4 whitespace-nowrap">{member.email}</td>
                <td className="px-6 py-4 whitespace-nowrap capitalize">{member.role}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    member.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {member.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleUpdateMember(member.id)}
                      className="p-1 text-gray-500 hover:text-red-600"
                      title="Update"
                    >
                      <FiEdit />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(member)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                      title={member.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {member.isActive ? <FiEyeOff /> : <FiEye />}
                    </button>
                    <button
                      onClick={() => handleResetPassword(member.email)}
                      className="p-1 text-gray-500 hover:text-blue-600"
                      title="Reset Password"
                    >
                      <FiRefreshCw />
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member.id)}
                      className="p-1 text-gray-500 hover:text-red-600"
                      title="Delete"
                    >
                      <FiTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
